import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/auth-context'
import { SidebarProvider } from './contexts/sidebar-context'
import { I18nProvider } from './contexts/i18n-context'
import { ErrorBoundary } from './components/error-boundary'
import { Toaster } from './components/ui/toaster'
import { Toaster as SonnerToaster } from './components/ui/sonner'
import { OfflineScreen } from './components/offline-screen'
// import { CookieConsent } from './components/cookie-consent'
// import { useVisitorTracking, useInteractionTracking } from './hooks/use-visitor-tracking'
import Layout from './components/layout'
import { ProtectedRoute } from './components/protected-route'

// Lazy-loaded routes for code splitting and fast initialization
const HomePage = lazy(() => import('./pages/home'))
const LandingPage = lazy(() => import('./pages/landing'))
const AccommodationPage = lazy(() => import('./pages/landing/accommodation'))
const BarAndGrillPage = lazy(() => import('./pages/landing/bar-and-grill'))
const ToursPage = lazy(() => import('./pages/landing/tours'))
const ContactPage = lazy(() => import('./pages/landing/contact'))
const GalleryPage = lazy(() => import('./pages/landing/gallery'))
const RestaurantMenuPage = lazy(() => import('./pages/restaurant-menu'))
const Dashboard = lazy(() => import('./pages/dashboard'))
const Accounting = lazy(() => import('./pages/accounting'))
const Sales = lazy(() => import('./pages/sales'))
const Expenses = lazy(() => import('./pages/expenses'))
const PurchaseOrders = lazy(() => import('./pages/purchase-orders'))
const AccountsPayable = lazy(() => import('./pages/accounts-payable'))
const EnhancedAccountsPayable = lazy(() => import('./pages/enhanced-accounts-payable'))
const Banking = lazy(() => import('./pages/banking'))
const Inventory = lazy(() => import('./pages/inventory'))
const Reports = lazy(() => import('./pages/reports'))
const Settings = lazy(() => import('./pages/settings'))
const AIInsights = lazy(() => import('./pages/ai-insights'))
const EnhancedTransactionProcessing = lazy(() => import('./pages/enhanced-transaction-processing'))
const EnhancedJournalManagement = lazy(() => import('./pages/enhanced-journal-management'))
const CreditNotes = lazy(() => import('./pages/credit-notes'))
const PeriodClose = lazy(() => import('./pages/period-close'))
const RevenueRecognition = lazy(() => import('./pages/revenue-recognition'))
const FixedAssets = lazy(() => import('./pages/fixed-assets'))
const LlamaAI = lazy(() => import('./pages/llama-ai'))
const Tax = lazy(() => import('./pages/tax'))
const TaxCalculation = lazy(() => import('./pages/tax-calculation'))
const Clients = lazy(() => import('./pages/clients'))
const Security = lazy(() => import('./pages/security'))
const Companies = lazy(() => import('./pages/companies'))
const Help = lazy(() => import('./pages/help'))
const FinancialReports = lazy(() => import('./pages/financial-reports'))
const JournalNew = lazy(() => import('./pages/journal-new'))
const BudgetManagement = lazy(() => import('./pages/budget-management'))
const Documents = lazy(() => import('./pages/documents'))
const POS = lazy(() => import('./pages/pos'))
const JournalHub = lazy(() => import('./pages/journal-hub'))
const UnifiedApprovalHub = lazy(() => import('./pages/unified-approval-hub'))
const SupplierPortal = lazy(() => import('./pages/supplier-portal'))
const VendorsPage = lazy(() => import('./pages/vendors'))
const PayrollPage = lazy(() => import('./pages/payroll'))
const UsersPage = lazy(() => import('./pages/users'))
const Profile = lazy(() => import('./pages/profile'))
const HotelPage = lazy(() => import('./pages/hotel'))
const Login = lazy(() => import('./pages/auth/login'))
const Register = lazy(() => import('./pages/auth/register'))
const ForgotPassword = lazy(() => import('./pages/auth/forgot-password'))
const VerifyEmail = lazy(() => import('./pages/auth/verify-email'))
const Unauthorized = lazy(() => import('./pages/unauthorized'))
const MyAccount = lazy(() => import('./pages/my-account'))
const GuestDashboard = lazy(() => import('./pages/guest-dashboard'))
const FoodInventoryPage = lazy(() => import('./pages/food-inventory'))
const RoomDetailsPage = lazy(() => import('./pages/room-details'))
const Analytics = lazy(() => import('./pages/analytics'))

function PageLoadingFallback() {
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center p-8">
      <div className="flex flex-col items-center gap-3">
        <div className="h-9 w-9 animate-spin rounded-full border-3 border-primary/20 border-t-primary" />
        <span className="text-xs font-medium text-muted-foreground animate-pulse">Loading Monsterado Lounge...</span>
      </div>
    </div>
  )
}

function AppContent() {
  // Initialize visitor tracking
  // useVisitorTracking();
  // const { trackClick, trackError } = useInteractionTracking();

  return (
    <>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
        <Route path="/login" element={<Login />} />
        {/* <Route path="/register" element={<Register />} /> */}
        <Route path="/auth/login" element={<Login />} />
        {/* <Route path="/auth/register" element={<Register />} /> */}
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        {/* Landing page routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/accommodation" element={<AccommodationPage />} />
        <Route path="/room/:roomId" element={<RoomDetailsPage />} />
        <Route path="/bar-and-grill" element={<BarAndGrillPage />} />
        <Route path="/restaurant-menu" element={<RestaurantMenuPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/tours" element={<ToursPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/home" element={<HomePage />} />
        {/* Redirect /reports to /dashboard/reports */}
        <Route path="/reports" element={<Navigate to="/dashboard/reports" replace />} />
        {/* Redirect /documents to /dashboard/documents */}
        <Route path="/documents" element={<Navigate to="/dashboard/documents" replace />} />
        {/* Redirect /banking to /dashboard/banking */}
        <Route path="/banking" element={<Navigate to="/dashboard/banking" replace />} />
        <Route path="/my-account" element={<MyAccount />} />
        <Route path="/guest-dashboard" element={<GuestDashboard />} />
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'auditor', 'employee', 'manager', 'waiter', 'receptionist']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="accounting" element={<Accounting />} />
          <Route path="sales" element={<Sales />} />
          <Route path="pos" element={<POS />} />
          <Route path="journal-hub" element={<JournalHub />} />
          <Route path="approval-hub" element={<UnifiedApprovalHub />} />
          <Route path="supplier-portal" element={<SupplierPortal />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="payroll" element={<PayrollPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="hotel" element={<HotelPage />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="accounts-payable" element={<AccountsPayable />} />
          <Route path="enhanced-accounts-payable" element={<EnhancedAccountsPayable />} />
          <Route path="banking" element={<Banking />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="food-inventory" element={<FoodInventoryPage />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="profile" element={<Profile />} />
          <Route path="ai-insights" element={<AIInsights />} />
          <Route path="enhanced-transaction-processing" element={<EnhancedTransactionProcessing />} />
          <Route path="enhanced-journal-management" element={<EnhancedJournalManagement />} />
          <Route path="credit-notes" element={<CreditNotes />} />
          <Route path="period-close" element={<PeriodClose />} />
          <Route path="revenue-recognition" element={<RevenueRecognition />} />
          {/* <Route path="inventory-management" element={<InventoryManagement />} /> */}
          <Route path="fixed-assets" element={<FixedAssets />} />
          <Route path="llama-ai" element={<LlamaAI />} />
          <Route path="tax" element={<Tax />} />
          <Route path="tax-calculation" element={<TaxCalculation />} />
          <Route path="clients" element={<Clients />} />
          <Route path="companies" element={<Companies />} />
          <Route path="security" element={<Security />} />
          <Route path="help" element={<Help />} />
          <Route path="help-support" element={<Help />} />
          <Route path="financial-reports" element={<FinancialReports />} />
          {/* Alias to support sidebar link `/dashboard/financial-reporting` */}
          <Route path="financial-reporting" element={<FinancialReports />} />
          <Route path="budget-management" element={<BudgetManagement />} />
          <Route path="documents" element={<Documents />} />
          <Route path="journal/new" element={<JournalNew />} />
          <Route path="custom-report-builder" element={<Reports />} />
          <Route path="analytics" element={<Analytics />} />
          {/* Add more routes as needed */}
        </Route>
      </Routes>
      </Suspense>
      <Toaster />
      <SonnerToaster position="top-right" />
      {/* <CookieConsent /> */}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <I18nProvider defaultLanguage="en">
        <AuthProvider>
          <SidebarProvider>
            <AppContent />
            {/* Full-screen offline component with branding - takes over entire app */}
            <OfflineScreen showFullScreen={true} />
          </SidebarProvider>
        </AuthProvider>
      </I18nProvider>
    </ErrorBoundary>
  )
}

export default App
